/**
 * useOutboundMessageLifecycle
 * ----------------------------------------------------------------------------
 * Hook utilitário que encapsula o ciclo de vida de mensagens enviadas pelo
 * chat, substituindo acessos diretos do frontend às tabelas `messages` e
 * `message_attachments` por RPCs seguras (SECURITY DEFINER).
 *
 * IMPORTANTE: este hook NÃO substitui a edge function `send-message`.
 * O fluxo permanece:
 *   1) createPending(...)               → grava mensagem como 'pending'
 *   2) supabase.functions.invoke('send-message', ...)  ← INALTERADO
 *   3) markSent(...) ou markFailed(...) → grava resultado final
 *   4) attachMedia(...)                 → opcional, anexa arquivos em lote
 *
 * Toda chamada propaga `p_tenant_hint` com o tenant ativo do usuário.
 */
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

// Tipos de retorno da RPC create_outbound_message
export interface CreatedOutboundMessage {
  id: string;
  conversation_id: string;
  sender_type: string;
  sender_id: string | null;
  content: string | null;
  message_type: string;
  direction: string;
  status: string;
  reply_to_id: string | null;
  metadata: Record<string, unknown> | null;
  sent_at: string;
}

export interface CreatePendingParams {
  conversationId: string;
  messageType: string;
  content?: string | null;
  replyToId?: string | null;
  metadata?: Record<string, unknown> | null;
  senderId?: string | null;
}

export interface MarkSentParams {
  messageId: string;
  externalId?: string | null;
  /**
   * Patch opcional mesclado a `metadata` quando a mensagem é marcada como
   * enviada com sucesso. Usado pelo fluxo de reenvio para limpar erros
   * anteriores e gravar `resent_at` / `original_external_id`.
   */
  metadataPatch?: Record<string, unknown> | null;
}

export interface MarkFailedParams {
  messageId: string;
  // Aceita qualquer payload JSON-compatível (ex.: retorno de createErrorMetadata)
  errorPayload?: unknown;
}

export interface CreateSystemMessageParams {
  conversationId: string;
  content: string;
  senderId?: string | null;
  /**
   * Metadados opcionais gravados em `messages.metadata`.
   * Usado pela "Mensagem privada" (nota interna):
   * `{ kind: 'internal_note', author_id, author_name, mentions: [uuid] }`.
   */
  metadata?: Record<string, unknown> | null;
}


export interface AttachmentInput {
  type: string;
  url: string;
  filename?: string | null;
  size_bytes?: number | null;
  mime_type?: string | null;
  thumbnail_url?: string | null;
  metadata?: Record<string, unknown> | null;
}

export function useOutboundMessageLifecycle() {
  const { currentTenant } = useAuth();
  const tenantHint = currentTenant?.id ?? null;

  /**
   * Cria uma mensagem outbound com status 'pending' via RPC segura.
   * Substitui o INSERT direto em `messages` no frontend.
   */
  const createPending = useCallback(
    async (params: CreatePendingParams): Promise<CreatedOutboundMessage> => {
      const { data, error } = await supabase.rpc('create_outbound_message', {
        p_conversation_id: params.conversationId,
        p_message_type: params.messageType,
        p_content: params.content ?? null,
        p_reply_to_id: params.replyToId ?? null,
        p_metadata: (params.metadata ?? null) as never,
        p_sender_id_hint: params.senderId ?? null,
        p_tenant_hint: tenantHint,
      });

      if (error) {
        logger.error('OutboundMsg', 'createPending falhou', error);
        throw error;
      }

      // RPC retorna TABLE — pegamos a primeira linha
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        throw new Error('create_outbound_message não retornou linha');
      }
      return row as CreatedOutboundMessage;
    },
    [tenantHint]
  );

  /**
   * Marca a mensagem como enviada com sucesso após retorno do provider.
   * Aceita opcionalmente um `metadataPatch` que será mesclado em `metadata`
   * no banco (ex.: limpar erro anterior, gravar `resent_at`).
   */
  const markSent = useCallback(
    async (params: MarkSentParams): Promise<void> => {
      const { error } = await supabase.rpc('update_message_send_result', {
        p_message_id: params.messageId,
        p_success: true,
        p_external_id: params.externalId ?? null,
        p_error_payload: null,
        p_tenant_hint: tenantHint,
        p_metadata_patch: (params.metadataPatch ?? null) as never,
      });
      if (error) {
        logger.error('OutboundMsg', 'markSent falhou', error);
        throw error;
      }
    },
    [tenantHint]
  );

  /**
   * Marca a mensagem como falha de envio e mescla payload de erro em metadata.
   */
  const markFailed = useCallback(
    async (params: MarkFailedParams): Promise<void> => {
      const { error } = await supabase.rpc('update_message_send_result', {
        p_message_id: params.messageId,
        p_success: false,
        p_external_id: null,
        p_error_payload: (params.errorPayload ?? {}) as never,
        p_tenant_hint: tenantHint,
      });
      if (error) {
        logger.error('OutboundMsg', 'markFailed falhou', error);
        throw error;
      }
    },
    [tenantHint]
  );

  /**
   * Anexa arquivos a uma mensagem em lote via RPC segura.
   * Substitui INSERTs diretos em `message_attachments`.
   */
  const attachMedia = useCallback(
    async (messageId: string, attachments: AttachmentInput[]): Promise<number> => {
      if (!attachments || attachments.length === 0) return 0;
      const { data, error } = await supabase.rpc('attach_message_attachments', {
        p_message_id: messageId,
        p_attachments: attachments as never,
        p_tenant_hint: tenantHint,
      });
      if (error) {
        logger.error('OutboundMsg', 'attachMedia falhou', error);
        throw error;
      }
      return (data as number | null) ?? 0;
    },
    [tenantHint]
  );

  // ---------------------------------------------------------------------------
  // Reações, soft delete, retry e mensagens de sistema (Fase 4)
  // ---------------------------------------------------------------------------

  /**
   * Persiste o conjunto de reações de uma mensagem (substitui UPDATE direto).
   */
  const setReaction = useCallback(
    async (messageId: string, reactions: unknown): Promise<void> => {
      const { error } = await supabase.rpc('set_message_reaction', {
        p_message_id: messageId,
        p_reactions: (reactions ?? []) as never,
        p_tenant_hint: tenantHint,
      });
      if (error) {
        logger.error('OutboundMsg', 'setReaction falhou', error);
        throw error;
      }
    },
    [tenantHint]
  );

  /**
   * Marca a mensagem como apagada (soft delete). Retorna o `deleted_at`
   * gravado pelo banco para refletir no estado local.
   */
  const softDelete = useCallback(
    async (messageId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('soft_delete_message', {
        p_message_id: messageId,
        p_tenant_hint: tenantHint,
      });
      if (error) {
        logger.error('OutboundMsg', 'softDelete falhou', error);
        throw error;
      }
      return (data as string) ?? new Date().toISOString();
    },
    [tenantHint]
  );

  /**
   * Volta a mensagem ao status `pending` antes de invocar `send-message`
   * no fluxo de reenvio.
   */
  const resetForRetry = useCallback(
    async (messageId: string): Promise<void> => {
      const { error } = await supabase.rpc('reset_message_for_retry', {
        p_message_id: messageId,
        p_tenant_hint: tenantHint,
      });
      if (error) {
        logger.error('OutboundMsg', 'resetForRetry falhou', error);
        throw error;
      }
    },
    [tenantHint]
  );

  /**
   * Cria uma mensagem de sistema (sender_type='system', status='sent') —
   * usada em encerramento e claim de conversas de webchat.
   */
  const createSystem = useCallback(
    async (params: CreateSystemMessageParams): Promise<CreatedOutboundMessage> => {
      const { data, error } = await supabase.rpc('create_system_message', {
        p_conversation_id: params.conversationId,
        p_content: params.content,
        p_sender_id_hint: params.senderId ?? null,
        p_tenant_hint: tenantHint,
        // v1.1.0 — metadados opcionais (ex.: nota interna / mensagem privada)
        p_metadata: (params.metadata ?? null) as never,
      });

      if (error) {
        logger.error('OutboundMsg', 'createSystem falhou', error);
        throw error;
      }
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) throw new Error('create_system_message não retornou linha');
      return row as CreatedOutboundMessage;
    },
    [tenantHint]
  );

  return {
    createPending,
    markSent,
    markFailed,
    attachMedia,
    setReaction,
    softDelete,
    resetForRetry,
    createSystem,
  };
}
